import { Err, Ok, Result } from 'ts-res';
import {
  KameleoonError,
  KameleoonException,
  exhaustCheck,
} from 'src/kameleoonError';
import { Milliseconds, TrackingCacheItemType } from 'src/types';
import { CustomData } from 'src/kameleoonData/customData';
import { KameleoonData, KameleoonDataType } from 'src/kameleoonData/types';
import { CACHE_ITEM_LIFETIME } from 'src/constants';
import { CacheManager } from 'src/cacheManager';
import { VersionMatchType, TargetingType } from 'src/targeting/types';
import { FeatureFlagVariationsType } from 'src/storage/types';
import {
  CheckTargetingParametersType,
  ManageCacheParametersType,
  TimeUnit,
  IExternalPRNG,
} from './types';
import { HEX_CHARACTERS, NONCE_LENGTH } from './constants';
import { Validator } from './validator';
import { KameleoonLogger } from 'src/logging';
import { VariationByExpositionType } from 'src/clientConfiguration/types';

export class Utilities {
  static getNonce(prng: IExternalPRNG): string {
    let nonce: string = '';

    for (let i = 0; i < NONCE_LENGTH; i++) {
      const randomNumber = prng.getRandomNumber();
      const randomIndex = Math.floor(randomNumber * NONCE_LENGTH);
      nonce += HEX_CHARACTERS.charAt(randomIndex);
    }

    return nonce;
  }

  static checkTargeting({
    segment,
    visitorCode,
    targetingData,
    experimentId,
    variationConfiguration,
    clientConfiguration,
    packageInfo,
    dataManager,
  }: CheckTargetingParametersType): Result<boolean, KameleoonError> {
    KameleoonLogger.debug`CALL: checkTargeting(segment, visitorCode: ${visitorCode}, targetingData: ${targetingData}, experimentId: ${experimentId}, variationConfiguration, clientConfiguration, packageInfo, dataManager)`;
    if (!segment || !segment.conditionsData.firstLevel.length) {
      const result = Ok(true);
      KameleoonLogger.debug`RETURN checkTargeting(segment, visitorCode: ${visitorCode}, targetingData: ${targetingData}, experimentId: ${experimentId}, variationConfiguration, clientConfiguration, packageInfo, dataManager) -> (result: ${result})`;
      return result;
    }

    if (!variationConfiguration) {
      const result = Err(new KameleoonError(KameleoonException.Initialization));
      KameleoonLogger.debug`RETURN checkTargeting(segment, visitorCode: ${visitorCode}, targetingData: ${targetingData}, experimentId: ${experimentId}, variationConfiguration, clientConfiguration, packageInfo, dataManager) -> (result: ${result})`;
      return result;
    }

    let variations: FeatureFlagVariationsType = {};

    const assignedVariationsResult =
      variationConfiguration.getStoredVariations(visitorCode);

    if (assignedVariationsResult.ok) {
      variations = assignedVariationsResult.data;
    }

    const targetingTree = dataManager.getTree(segment);

    let trees = undefined;
    let ruleMap = undefined;
    let segments = undefined;

    // --- Note ---
    // Only getting trees and segments targeting type
    if (targetingTree.hasTargetingType(TargetingType.SEGMENT)) {
      trees = dataManager.trees;
      segments = clientConfiguration.segments;
    }

    // --- Note ---
    // Only constructing rule map for targeting type
    if (targetingTree.hasTargetingType(TargetingType.TARGET_FEATURE_FLAG)) {
      ruleMap = clientConfiguration.ruleMap;
    }

    const result = targetingTree.evaluate({
      variationData: variations,
      sdkInfo: packageInfo,
      segments,
      ruleMap,
      trees,
      targetingData,
      experimentId,
      visitorCode,
    });

    KameleoonLogger.debug`RETURN checkTargeting(segment, visitorCode: ${visitorCode}, targetingData: ${targetingData}, experimentId: ${experimentId}, variationConfiguration, clientConfiguration, packageInfo, dataManager) -> (result: ${result})`;
    return result;
  }

  static convertTimeUnit(time: number, timeUnit: TimeUnit): number {
    const timeMap = {
      [TimeUnit.Minute]: Milliseconds.Minute,
      [TimeUnit.Hour]: Milliseconds.Hour,
      [TimeUnit.Day]: Milliseconds.Day,
      [TimeUnit.Week]: Milliseconds.Week,
      [TimeUnit.Month]: Milliseconds.Month,
    };

    return time / timeMap[timeUnit];
  }

  static isCustomData(dataItem: KameleoonDataType): dataItem is CustomData {
    return dataItem.data.type === KameleoonData.CustomData;
  }

  static updateCache({
    hasJsCssVariables,
    cacheManager,
    visitorCode,
    experimentId,
    variationId,
  }: ManageCacheParametersType): void {
    const cacheData = cacheManager.getAliveItem(visitorCode);

    const newCacheData = cacheData ? { ...cacheData } : {};

    newCacheData[experimentId] = {
      variationId,
      hasJsCssVariables,
      expirationTime: Date.now() + CACHE_ITEM_LIFETIME * Milliseconds.Second,
    };

    cacheManager.add({
      key: visitorCode,
      data: newCacheData,
      lifetime: CACHE_ITEM_LIFETIME,
    });
  }

  static getTrackingCode(
    trackingCache: CacheManager<TrackingCacheItemType>,
    visitorCode: string,
  ): string {
    let resultString = 'window.kameleoonQueue=window.kameleoonQueue||[];';

    const cacheData = trackingCache.getAliveItem(visitorCode);

    if (!cacheData) {
      return resultString;
    }

    const updatedCacheData: TrackingCacheItemType = {};

    Object.entries(cacheData).forEach(
      ([experimentId, { variationId, expirationTime, hasJsCssVariables }]) => {
        const flag = !hasJsCssVariables;

        if (expirationTime > Date.now()) {
          resultString += `window.kameleoonQueue.push(['Experiments.assignVariation',${experimentId},${variationId},true]);`;
          resultString += `window.kameleoonQueue.push(['Experiments.trigger',${experimentId},${flag}]);`;

          updatedCacheData[Number(experimentId)] = {
            variationId,
            expirationTime,
            hasJsCssVariables,
          };
        }
      },
    );

    // --- Note ---
    // Cache is updated with only alive experiments
    trackingCache.add({
      key: visitorCode,
      data: updatedCacheData,
      lifetime: CACHE_ITEM_LIFETIME,
    });

    return resultString;
  }

  static compareSemVer({
    version,
    compareVersion,
    operator,
  }: {
    version: string;
    compareVersion: string;
    operator: VersionMatchType;
  }): Result<boolean, KameleoonError> {
    const versionResult = Validator.validateVersion(version);
    const compareVersionResult = Validator.validateVersion(compareVersion);

    if (!versionResult.ok) {
      return Err(versionResult.error);
    }

    if (!compareVersionResult.ok) {
      return Err(compareVersionResult.error);
    }

    const [major, minor, patch] = versionResult.data;
    const [compareMajor, compareMinor, comparePatch] =
      compareVersionResult.data;

    const [isMajorEqual, isMinorEqual, isPatchEqual] = [
      major === compareMajor,
      minor === compareMinor,
      patch === comparePatch,
    ];

    switch (operator) {
      case VersionMatchType.EQUAL:
        return Ok(isMajorEqual && isMinorEqual && isPatchEqual);
      case VersionMatchType.GREATER:
        return Ok(
          major > compareMajor ||
            (isMajorEqual && minor > compareMinor) ||
            (isMajorEqual && isMinorEqual && patch > comparePatch),
        );
      case VersionMatchType.LOWER:
        return Ok(
          major < compareMajor ||
            (isMajorEqual && minor < compareMinor) ||
            (isMajorEqual && isMinorEqual && patch < comparePatch),
        );
      default:
        KameleoonLogger.error`Unknown version match type: ${operator}`;
        exhaustCheck(operator);
    }
  }

  static getVariationByHash(
    variationByExposition: VariationByExpositionType[],
    hash: number,
  ): VariationByExpositionType | null {
    let total = 0;
    for (const varByExpItem of variationByExposition) {
      total += varByExpItem.exposition;
      if (total >= hash) {
        return varByExpItem;
      }
    }
    return null;
  }
}
